/**
 * Schemas for ReploAgent Durable Object WebSocket messages to frontend clients.
 */
import { z } from "zod";

import { opencodeEventSchema } from "./opencode";

export const processTypeSchema = z
  .enum(["opencode", "nextjs", "supervisor", "workspace-init"])
  .describe("ProcessType");

export const logLevelSchema = z
  .enum(["debug", "info", "warn", "error", "fatal"])
  .describe("LogLevel");

export const processLogPayloadSchema = z
  .object({
    sandboxId: z.string(),
    processType: processTypeSchema,
    level: logLevelSchema,
    message: z.string(),
    timestamp: z.number(),
  })
  .describe("ProcessLogPayload");

export const processLogMessageSchema = z
  .object({
    type: z.literal("process-log"),
    payload: processLogPayloadSchema,
  })
  .describe("ProcessLogMessage");

export const sandboxStatusSchema = z
  .enum([
    "startingSandbox",
    "waitingForSupervisor",
    "initializingWorkspace",
    "startingServices",
    "sendingMessage",
    "streaming",
  ])
  .describe("SandboxStatus");

export const streamingSessionStateSchema = z
  .object({
    previewUrl: z.string().optional(),
    previewingSha: z.string().optional(),
    originalSha: z.string().optional(),
    sandboxStatus: sandboxStatusSchema.optional(),
    // NOTE (Kevin O'Connell, 2026-03-10, REPL-22983): providerSandboxId is the
    // provider-specific sandbox instance ID (e.g. Modal's sb-xxx object ID or
    // Cloudflare's container instance ID). modalSandboxAppId is Modal's
    // application/deployment ID that groups sandboxes (e.g. app ID like ap-xxx).
    // Both are optional because they are populated
    // asynchronously after sandbox creation and may not be immediately available
    // during the initial state-sync.
    providerSandboxId: z.string().optional(),
    modalSandboxAppId: z.string().optional(),
    sandboxType: z.enum(["cloudflare", "modal"]).optional(),
  })
  .describe("StreamingSessionState");

export const publishSyncStatusSchema = z
  .object({
    status: z.enum(["idle", "building", "publishing", "published", "failed"]),
    startedAt: z.number(),
    completedAt: z.number().optional(),
    errorMessage: z.string().optional(),
  })
  .describe("PublishSyncStatus");

export const agentStateSyncPayloadSchema = z
  .object({
    streamingSessionState: z
      .record(z.string(), streamingSessionStateSchema)
      .optional(),
    publishStatus: publishSyncStatusSchema.optional(),
    durableObjectId: z.string().optional(),
  })
  .describe("AgentStateSyncPayload");

export const agentStateSyncMessageSchema = z
  .object({
    type: z.literal("state-sync"),
    state: agentStateSyncPayloadSchema,
  })
  .describe("AgentStateSyncMessage");

export const opencodeSessionTypeSchema = z
  .enum(["main", "subagent", "side"])
  .describe("OpencodeSessionType");

export const opencodeEventMessageSchema = z
  .object({
    type: z.literal("opencode-event"),
    payload: opencodeEventSchema,
  })
  .describe("OpencodeEventMessage");

export const memoryStatsProcessInfoSchema = z
  .object({
    pid: z.number(),
    rssBytes: z.number(),
  })
  .describe("MemoryStatsProcessInfo");

export const memoryStatsPayloadSchema = z
  .object({
    sandboxId: z.string(),
    timestamp: z.number(),
    containerCurrentBytes: z.number(),
    containerMaxBytes: z.number(),
    cgroupStats: z.record(z.string(), z.number()),
    processes: z.record(z.string(), memoryStatsProcessInfoSchema),
  })
  .describe("MemoryStatsPayload");

export const memoryStatsMessageSchema = z
  .object({
    type: z.literal("memory-stats"),
    payload: memoryStatsPayloadSchema,
  })
  .describe("MemoryStatsMessage");

// NOTE (Max, 2026-03-09): Represents an uploaded file sent from the frontend to the DO
// as part of a sessionPrompt RPC call. The frontend uploads files to Cloudflare via
// batchUploadToCloudflare, then maps the upload results to this schema. The DO then
// converts these into opencode FilePartInput objects for promptAsync.
export const sessionPromptFileSchema = z
  .object({
    url: z.string(),
    mime: z.string(),
    filename: z.string(),
  })
  .describe("SessionPromptFile");

export const agentToClientMessageSchema = z
  .discriminatedUnion("type", [
    opencodeEventMessageSchema,
    agentStateSyncMessageSchema,
    processLogMessageSchema,
    memoryStatsMessageSchema,
  ])
  .describe("AgentToClientMessage");
